import React, { useEffect, useRef, useState } from "react";

import '@babylonjs/loaders';
// // Side-effects only imports allowing the standard material to be used as default.
import '@babylonjs/core/Materials/standardMaterial';
// // Side-effects only imports allowing Mesh to create default shapes (to enhance tree shaking, the construction methods on mesh are not available if the meshbuilder has not been imported).
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/groundBuilder';
import earcut from 'earcut';
import * as _ from 'lodash';
import {
  ActionManager, AssetContainer, ExecuteCodeAction, PointerEventTypes, SceneLoader, ActionEvent, MeshBuilder, StandardMaterial, AbstractMesh, DynamicTexture, Mesh, MirrorTexture, SpotLight, HemisphericLight, Observable, PointerInfo, Nullable, TransformNode,
} from '@babylonjs/core';
import { UniversalCamera } from '@babylonjs/core/Cameras';
import { Engine } from '@babylonjs/core/Engines/engine';
import { Vector3, Vector2, Color3 } from '@babylonjs/core/Maths/math';
import { Scene } from '@babylonjs/core/scene';

import { createLight, createUniversalCamera, createSky, createGroundColor, createGround, createGroundReflectiveTexture, createWelcomeBox, createStage } from "../Utils/worldUtils";
import { addVector2, getDistance, zeroBounds, getBoundsForWorld, calculateAngleInfoFromSourceToTarget, isPointIntersectingBox } from "./mathUtils";
import { SetUserPositionAndRotationWorldMessage, WorldMessage, AppMessage, Seat, YouTubeMedia, SetYouTubeMedia } from "./Types";
import { UserMeshType, UserMeshBasic, UserMeshMinimal, MeshesForUser } from "./UserMesh";
import { fixChromeEchoCancellationForAudioNode } from "./audioUtils";
import { User } from "./User";
import { UserSound } from "./UserSound";

import './World.css';

import SongTest from '../../../assets/test.mp3';
import VoiceTest from '../../../assets/voice-test.mp3';
import VoiceTest2 from '../../../assets/voice-test2.mp3';
import VoiceTest3 from '../../../assets/voice-test3.mp3';
import VoiceTest4 from '../../../assets/voice-test4.mp3';

import HeadStl from '../../../assets/head.stl';
import TorsoStl from '../../../assets/torso.stl';

import { GradientMaterial } from "@babylonjs/materials";
import { useParticipantIds, useAppMessage, useDaily, useCallObject, useLocalSessionId } from "@daily-co/daily-react";
import { DailyEventObjectParticipant, DailyEventObjectParticipantLeft, DailyEventObjectAppMessage } from "@daily-co/daily-js";
import { useCameraControls, CameraControlsContextValue } from "../../react/context/CameraControlsCallbackContext";
import { getIsSlowSettingEnabled, getIsDebugEnabled } from "../../../services/LocalStorage";
import { useFPS } from "../../react/context/FpsContext";
import { updateSeatPositions, createSeats, createNewSeat, createSeatForTable } from "../Utils/tableUtils";
import { SetUserTableValueWorldMessage, TableValue } from "../../../models/Types";
import { UserMeshManager } from "./UserMeshManager";
// import { useCameraControls, CameraControlsContextValue } from "../../react/context/CameraControlsContext";

import { YouTubePlane } from './IFrame/YouTubePlane';
import { useMediaPlayerControls } from "../../react/context/MediaPlayerContext";

interface WorldProps {
  onShouldEnableMic: (shouldEnable: boolean) => Promise<void>;
  onShouldEnableCamera: (shouldEnable: boolean) => Promise<void>;
  // moveForward: boolean;
  // moveBackward: boolean;
  // turnLeft: boolean;
  // turnRight: boolean;
}

declare const window: any;

const enableTables = false;

const World: React.FC<WorldProps> = ({ onShouldEnableMic, onShouldEnableCamera }) => {
  const canvasRef = useRef(null);
  const engineRef = useRef<Nullable<Engine>>(null);
  const sceneRef = useRef<Nullable<Scene>>(null);

  const limitQuality = useRef(getIsSlowSettingEnabled());

  const userMeshType = useRef<UserMeshType>('UserMeshMinimal');

  const localParticipantId = useLocalSessionId();

  const activeInputs = useRef<{ [key: string]: boolean }>({});
  const pointerObservable = useRef<Nullable<Observable<PointerInfo>>>(null);

  const movement = useRef(new Vector2(0,0));
  const rotation = useRef(new Vector2(0,0));
  const mouseRotation = useRef(new Vector2(0,0));

  const isMovementKeyPressed = useRef(false);
  const isRotationKeyPressed = useRef(false);
  const isMouseRotating = useRef(false);

  const boundsForWorld = useRef(zeroBounds);

  const isFocusedSoundMode = useRef(false);
  const focusedUserId = useRef(localParticipantId);
  const focusedUserIdIterator = useRef<string[]>([]);
  const focusedUserIndicator = useRef<AbstractMesh>();

  const prevRenderTime = useRef(0);
  const framesInCurrentCycle = useRef(0);
  const cumulativeRenderTime = useRef(0);
  const currentFPS = useRef(60);
  const rollingFPS = useRef<number[]>([60,60,60,60,60,60,60,60,60,60]);
  const { updateFPS } = useFPS();

  const ytPlane = useRef<YouTubePlane | null>(null);
  const ytPlane2 = useRef<YouTubePlane | null>(null);

  const camera = useRef<UniversalCamera>(null!);
  const groundReflectiveTexture = useRef<MirrorTexture>(null!);
  const ground = useRef<Mesh>(null!);
  const sky = useRef<Mesh>(null!);
  const sunLight = useRef<HemisphericLight>(null!);
  const moonLight = useRef<SpotLight>(null!);
  const stage = useRef<Mesh>(null!);

  const materialForUserMesh = useRef<StandardMaterial>(null!);

  const userMeshManager = useRef<UserMeshManager>(null!);

  const cameraDirection = useRef(new Vector3(0, 0, 0));
  const cameraHeight = useRef(1);
  const lastSentPositionAndRotationMessage = useRef<SetUserPositionAndRotationWorldMessage>({
    messageType: 'world',
    worldMessageType: 'set-user-position-and-rotation',
    position: new Vector2(0,0),
    headRotation: new Vector2(0,0),
    movement: new Vector2(0,0),
  });
  const assetContainers = useRef<{ [id: string]: AssetContainer }>({});

  const doesAudioEngineNeedLoopbackFix = useRef(false);

  const camControls = useCameraControls();


  const { onUpdateYouTubeVideoId, onSetPlaybackRate, onSetVolume, onSetSeek, onSetMute, onSetPause, updatedYouTubeVideoId } = useMediaPlayerControls();
  
  useEffect(() => {
    const unsubscribeSetVideoId = onUpdateYouTubeVideoId((videoId) => {
      updateYouTubePlane(videoId);
    });

    const unsubscribeSetPlaybackRate = onSetPlaybackRate((rate) => {
      if (ytPlane.current) {
        ytPlane.current.setPlaybackRate(rate);
      }
    });

    const unsubscribeSetVolume = onSetVolume((vol) => {
      if (ytPlane.current) {
        ytPlane.current.updateVolume(vol);
      }
    });

    const unsubscribeSetSeek = onSetSeek((seek) => {
      if (ytPlane.current) {
        ytPlane.current.seekTo(seek);
      }
    });

    const unsubscribeSetMute = onSetMute((mute) => {
      if (ytPlane.current) {
        ytPlane.current.setMuted(mute);
      }
    });

    const unsubscribeSetPause = onSetPause((pause) => {
      if (ytPlane.current) {
        ytPlane.current.setPause(pause);
      }
    });

    return () => {
      unsubscribeSetVideoId();
      unsubscribeSetPlaybackRate();
      unsubscribeSetVolume();
      unsubscribeSetSeek();
      unsubscribeSetMute();
      unsubscribeSetPause();
    };
  }, [onUpdateYouTubeVideoId, onSetPlaybackRate, onSetVolume, onSetSeek]);

  const updateYouTubePlane = (videoId: string) => {
    if (ytPlane.current) {
      ytPlane.current.updateVideoId(videoId);
      ytPlane.current.plane.position = new Vector3(3,4,-5);

      const publishYouTubeMedia = async () => {
        if (ytPlane.current) {
          const result = await ytPlane.current.getYouTubeMedia();  
          updatedYouTubeVideoId(result);
        }
      }
      publishYouTubeMedia();
    }
  }

  /* Table Functions */
  const tableSeats = useRef<Seat[]>([]);
  const seatForTable = useRef<Mesh>(null!);
  const localSeat = useRef<number>(-1);

  const isGetInitialStateMessageSent = useRef(true);
  const hasReceivedInitialStateFromAllRemoteUsers = useRef(false);

  const updateTable = () => {
    if (!enableTables) return;
    if (sceneRef.current === null ) return;
    const numUsersInCall = userMeshManager.current.getNumUsers() + 1;
    while (tableSeats.current.length < numUsersInCall && numUsersInCall >= 2) {
      addSeatToTable();
      
      if (localSeat.current !== -1) {
        moveUserToSeat(localSeat.current);
        notifyOthersAboutLocalTableUpdate(localSeat.current);
      }
    }
  }

  const getSeatForTable = (index: number) => {
    if (!seatForTable.current) seatForTable.current = createSeatForTable(sceneRef.current!, materialForUserMesh.current);
    return index === 0 ? seatForTable.current : seatForTable.current.createInstance(`seat.${index}`);
  }

  const addSeatToTable = () => {
    const newSeatPositions = updateSeatPositions(tableSeats.current.length + 1);
    for (let i=0; i<tableSeats.current.length; i++) {
      tableSeats.current[i].seat.position = newSeatPositions[i];
    }
    const index = newSeatPositions.length - 1;
    const seatMesh = getSeatForTable(index);
    const newSeat = createNewSeat(newSeatPositions[index], seatMesh);
    tableSeats.current.push(newSeat);
  }

  const moveUserToSeat = (seatIndex: number, userId = userMeshManager.current.localUserId) => {
    const tableSeat = tableSeats.current[seatIndex];
    tableSeat.userId = userId;
    tableSeat.seat.position.y = -0.01;
    const position = tableSeat.seat.position.clone();
    position.y = cameraHeight.current;
    if (userId !== userMeshManager.current.localUserId) return;
    camera.current.position = position;
    localSeat.current = seatIndex;
  }

  const rotateLocalUserToTableCenter = () => {
    const angleBetweenCameraAndTableCenter = calculateAngleInfoFromSourceToTarget(camera.current.position, camera.current.rotation, new Vector3(0,0,0));
    camera.current.rotation.y = camera.current.rotation.y + angleBetweenCameraAndTableCenter.clockwiseAngleRadians;
  }

  const notifyOthersAboutLocalTableUpdate = (seatIndex: number) => {
    sendUserPositionAndRotationMessageToUsers({ shouldForce: true });
    const tableValue: TableValue = {
      userId: userMeshManager.current.localUserId,
      position: {
        seatIndex,
        tableIndex: 0,
      }
    }
    sendUserTableValueMessageToUsers(tableValue);
  }

  const seatClicked = (seatIndex: number, distance: number, userId = userMeshManager.current.localUserId) => {
    if (!enableTables) return;
    const tableSeat = tableSeats.current[seatIndex];
    if (tableSeat.userId !== '' || distance > 11) return;
    vacateSeat(userId);

    moveUserToSeat(seatIndex, userId);
    if (userId === userMeshManager.current.localUserId) {
      rotateLocalUserToTableCenter();
      notifyOthersAboutLocalTableUpdate(seatIndex);
    }
  }

  const vacateSeat = (userId = userMeshManager.current.localUserId) => {
    if (userId === '') return;
    const occupiedSeatIndex = tableSeats.current.findIndex(seat => seat.userId === userId);
    if (occupiedSeatIndex === -1) return;
    const occupiedSeat = tableSeats.current[occupiedSeatIndex];
    occupiedSeat.userId = '';
    occupiedSeat.seat.position.y = 0.01;
    if (userId !== userMeshManager.current.localUserId) return;
    localSeat.current = -1;
    const tableValue: TableValue = { position: { tableIndex: 0, seatIndex: -1}, userId };
    sendUserTableValueMessageToUsers(tableValue);
  }
  /* End of Table Functions */

  console.log('babylon world re-rendered');
  const callObject = useDaily();

  useEffect(() => {
    if (!callObject) return;
    callObject.on('participant-joined', handleParticipantJoined);
    callObject.on('participant-updated', handleParticipantUpdated);
    callObject.on('participant-left', handleParticipantLeft);

    return () => {
      callObject.off('participant-joined', handleParticipantJoined);
      callObject.off('participant-updated', handleParticipantUpdated);
      callObject.off('participant-left', handleParticipantLeft);
    }
  }, [callObject]);

  useEffect(() => {
    if (!camControls) return;

    const handleMoveForward = (isActive: boolean) => {
      activeInputs.current['w'] = isActive;
      updateIsMovementOrRotationKeyPressed();
    };
    const handleMoveBackward = (isActive: boolean) => {
      activeInputs.current['s'] = isActive;
      updateIsMovementOrRotationKeyPressed();
    };
    const handleTurnLeft = (isActive: boolean) => {
      activeInputs.current['arrowleft'] = isActive;
      updateIsMovementOrRotationKeyPressed();
    };
    const handleTurnRight = (isActive: boolean) => {
      activeInputs.current['arrowright'] = isActive;
      updateIsMovementOrRotationKeyPressed();
    };

    const moveForwardSubscription = camControls.onMoveForward(handleMoveForward);
    const moveBackardSubscription = camControls.onMoveBackward(handleMoveBackward);
    const turnLeftSubscription = camControls.onTurnLeft(handleTurnLeft);
    const turnRightSubscription = camControls.onTurnRight(handleTurnRight);

    return () => {
      moveForwardSubscription();
      moveBackardSubscription();
      turnLeftSubscription();
      turnRightSubscription();
    };
    
  }, [camControls]);

  // const participantKey = 'user_id';
  const participantKey = 'session_id';

  const sendAppMessage = useAppMessage({ onAppMessage: (ev: DailyEventObjectAppMessage) => handleAppMessage(ev)});

  const emptyRoomDisplayMessage = useRef<Nullable<Mesh>>(null);

  const updateEmptyRoomDisplay = () => {
    if (sceneRef.current === null) return;
    const numRemoteUsers = userMeshManager.current.getNumUsers();
    if (numRemoteUsers > 0 && emptyRoomDisplayMessage.current !== null) {
      sceneRef.current.removeMesh(emptyRoomDisplayMessage.current);
      emptyRoomDisplayMessage.current.material?.dispose();
      emptyRoomDisplayMessage.current.dispose();
      emptyRoomDisplayMessage.current = null;
    }
    if (numRemoteUsers === 0 && emptyRoomDisplayMessage.current === null) {
      emptyRoomDisplayMessage.current = createWelcomeBox(sceneRef.current);
      emptyRoomDisplayMessage.current.position = new Vector3(0, 1, 0);
    }
  }

  const handleParticipantJoined = (p: DailyEventObjectParticipant) => {
    // User: constructor(userId: string, userName: string, isLocal: boolean, audioTrack?: MediaStreamTrack, videoTrack?: MediaStreamTrack)
    const user = new User(p.participant[participantKey], p.participant.user_name, p.participant.local, p.participant.owner, p.participant.tracks.audio.persistentTrack, p.participant.tracks.video.persistentTrack);
    if (p.participant.joined_at) user.joinedAt = p.participant.joined_at;
    console.log('p joined: ', p);
    createUser(user);
    updateEmptyRoomDisplay();
  }

  const handleParticipantUpdated = (p: DailyEventObjectParticipant) => {
    const user = new User(p.participant[participantKey], p.participant.user_name, p.participant.local, p.participant.owner, p.participant.tracks.audio.persistentTrack, p.participant.tracks.video.persistentTrack);
    console.log('p updated', p);
    if (user.isLocal) return createUser(user);
    const meshForUser = userMeshManager.current.getUserMesh(user.id);
    if(!meshForUser) return; // should we create a partial user here?
    const isNewVideoTrack = user.videoTrack && (!meshForUser.user.videoTrack || meshForUser.user.videoTrack.id !== user.videoTrack.id);
    if (isNewVideoTrack) {
      meshForUser.user.videoTrack = user.videoTrack;
      userMeshManager.current.attachVideoStreamToMesh(user.id, new MediaStream([user.videoTrack!]))
    }
    meshForUser.user.audioTrack = user.audioTrack;
    if (p.participant.joined_at) meshForUser.user.joinedAt = p.participant.joined_at;
    if (user.audioTrack) {
      const audioStream = new MediaStream([user.audioTrack]);
      userMeshManager.current.attachAudioStreamToMesh(user.id, audioStream);
    }
  }

  const handleParticipantLeft = (p: DailyEventObjectParticipantLeft) => {
    const userId = p.participant[participantKey];
    vacateSeat(userId);
    removeUserMesh(userId);
    updateEmptyRoomDisplay();
  }

  const handleAppMessage = (m: DailyEventObjectAppMessage) => {
    if (m.data.messageType !== 'world') return;
    if (m.data.worldMessageType === 'set-user-position-and-rotation') {
      const message: SetUserPositionAndRotationWorldMessage = {
        messageType: 'world',
        worldMessageType: 'set-user-position-and-rotation',
        headRotation: m.data.headRotation,
        movement: m.data.movement,
        position: m.data.position
      }
      const appMessage: AppMessage = {
        userId: m.fromId,
        message
      };
      handleWorldUserMessage(appMessage.userId, appMessage.message);
    }
    if (m.data.worldMessageType === 'set-user-table-value') {
      const message: SetUserTableValueWorldMessage = {
        messageType: 'world',
        worldMessageType: 'set-user-table-value',
        tablePosition: m.data.tablePosition,
        seatedAt: m.data.seatedAt,
      }
      console.log('got set user table value message for user: ', message.tablePosition.userId, ' for seat: ', message.tablePosition.position.seatIndex);
      if (message.tablePosition.position.seatIndex === -1) {
        vacateSeat(message.tablePosition.userId);
      } else {
        moveUserToSeat(message.tablePosition.position.seatIndex, message.tablePosition.userId)
        // seatClicked(message.tablePosition.position.seatIndex, 0, message.tablePosition.userId);
      }
      const user = userMeshManager.current.getUserMesh(m.fromId)?.user;
      // const user = remoteUserMesh.current[m.fromId]?.user;
      if (user) user.hasReceivedInitialState = true;
    }
    if (m.data.worldMessageType === 'get-initial-state') {
      sendInitialStateMessage(m.fromId);
    }
    if (m.data.worldMessageType === 'set-youtube-media' && m.fromId !== localParticipantId) {
      if (ytPlane.current) {
        updateYouTubePlane(m.data.videoId);  
        ytPlane.current.setPlaybackRate(m.data.playbackRate);
        ytPlane.current.seekTo(m.data.currentSeek);
        ytPlane.current.setPause(m.data.isPaused);
      }


    }
  }

  const sendInitialStateMessage = (userId: string) => {
    sendUserPositionAndRotationMessageToUsers({ userId, shouldForce: true, });
    if (localSeat.current !== -1) {
      const tableValue: TableValue = {
        userId: userMeshManager.current.localUserId,
        position: {
          tableIndex: 0,
          seatIndex: localSeat.current
        }
      }
      sendUserTableValueMessageToUsers(tableValue, userId);
    }
  }

  useEffect(() => {
    Object.assign(window, { Vector3, earcut, test: { canvasRef, sceneRef, userMesh: userMeshManager } });

    doesAudioEngineNeedLoopbackFix.current = navigator.userAgent.indexOf('Chrome') !== -1;

    engineRef.current = new Engine(canvasRef.current, true);
    sceneRef.current = new Scene(engineRef.current);
    userMeshManager.current = new UserMeshManager(sceneRef.current);
    userMeshManager.current.localUserId = localParticipantId;

    // void loadAssets(); // can't async this method, may need to look into/fix this
    const NOT_CONNECTED_USER = new User('localUserId', 'userName', true, false);
    createUserMeshes();
    createEnvironment();
    createInputListeners();
    createUser(NOT_CONNECTED_USER);
    // if (getIsDebugEnabled()) createDummyUsers(sceneRef.current);
    
    engineRef.current.runRenderLoop(() => {
      render();
    });

    return () => {
      // console.log('deleting scene and engine: ', sceneRef.current);
      sceneRef.current?.onPointerObservable.removeCallback(onPointerObservableCallback);
      sceneRef.current?.unregisterBeforeRender(render);
      engineRef.current?.stopRenderLoop();
      sceneRef.current?.dispose();
      engineRef.current?.dispose();
      sceneRef.current = null;
      engineRef.current = null;
    };
  }, []);

  const createUserMeshes = () => {
    if (sceneRef.current === null) return;
    materialForUserMesh.current = userMeshManager.current.userMaterial!;
    // materialForUserMesh.current = createMaterialForUserMesh(sceneRef.current);
  }

  const createEnvironment = () => {
    if (sceneRef.current === null) return;
    camera.current = createUniversalCamera(sceneRef.current);
    camera.current.attachControl(canvasRef.current, true);
    camera.current.rotation.x = 0;
    const lights = createLight(sceneRef.current, camera.current);
    sunLight.current = lights.sunLight;
    moonLight.current = lights.moonLight;
    sky.current = createSky(sceneRef.current);
    groundReflectiveTexture.current = createGroundReflectiveTexture(sceneRef.current);
    ground.current = createGround(sceneRef.current, groundReflectiveTexture.current);
    boundsForWorld.current = getBoundsForWorld(sceneRef.current.getWorldExtends());
    // stage.current = createStage(sceneRef.current);

    if (engineRef.current) {
      ytPlane.current = new YouTubePlane(engineRef.current, sceneRef.current, 'afxZYl42x5o');
      ytPlane.current.plane.position = new Vector3(3,4,-5);
      // ytPlane.current.setup();

      // ytPlane2.current = new YouTubePlane(engineRef.current, sceneRef.current, '7IrbZUQnl_0');
      // ytPlane2.current.plane.position.z = -50;
      // ytPlane2.current.plane.position.y = 4;
      // ytPlane2.current.plane.position.x = 30;
      // ytPlane2.current.setup();
      // const yt2 = new YouTubePlane(engineRef.current, sceneRef.current, 'NIwRJjk-LzI');
      // yt2.plane.position.z = -50;
      // yt2.plane.position.y = 4;
      // yt2.plane.position.x = 30;
      // yt2.setup();

    }
    // console.log(stage.current.getBoundingInfo());
    // seatForTable.current = createSeatForTable(sceneRef.current, materialForUserMesh.current);
  }

  useEffect(() => {
    return () => {
      for (const userId of userMeshManager.current.getUserIds()) {
        removeUserMesh(userId);
      }
      for (const seat of tableSeats.current) {
        seat.seat.dispose();
      }
      seatForTable.current?.dispose();
      userMeshManager.current.dispose();
      materialForUserMesh.current?.dispose();
      sunLight.current.dispose();
      moonLight.current.dispose();
      if (sky.current.material) sky.current.material.dispose();
      sky.current.dispose();
      groundReflectiveTexture.current.dispose();
      if (ground.current.material) ground.current.material.dispose();
      ground.current.dispose();
      camera.current.dispose();
    }
  }, []);

  const removeUserMesh = (userId: string) => {
    userMeshManager.current.removeUser(userId);
    if (sceneRef.current !== null) {
      if (focusedUserId.current === userId) {
        focusedUserId.current = userMeshManager.current.localUserId;
        setFocusedVolumeForSound();
      }
      // removeAllUserMeshFromScene(meshForUser);
      addUserMeshToReflectiveFloor();
    }
  }

  const createUser = (user: User) => {
    if (sceneRef.current === null) return console.error('handleUserJoined: scene is null');

    if (user.isLocal) {
      userMeshManager.current.localUserId = user.id;
      focusedUserId.current = user.id;
      userMeshManager.current.localUserId = user.id;
      // console.log('set local user id to ', user.id);
      if (camera.current !== null) {
        cameraHeight.current = userMeshType.current === 'UserMesh' ? 2 : 1.4;
        camera.current.setTarget(new Vector3(0, cameraHeight.current, 0));
        adjustCamera();
      } else {
        console.log('null camera on create user');
      }

      isGetInitialStateMessageSent.current = false;
      updateEmptyRoomDisplay();
    } else  {
      sendInitialStateMessage(user.id);
      const meshForUser = userMeshManager.current.addUser(user);
      addUserMeshToReflectiveFloor();

    }
    updateTable();
    sendUserPositionAndRotationMessageToUsers({ shouldForce: true });
    if (doesAudioEngineNeedLoopbackFix.current) {
      const audioContext = Engine.audioEngine?.audioContext;
      const masterGain = Engine.audioEngine?.masterGain;
      if (audioContext && masterGain) {
        fixChromeEchoCancellationForAudioNode(audioContext, masterGain);
      }
      doesAudioEngineNeedLoopbackFix.current = false;
    }
  }

  const sendGetInitialStateMessage = () => {
    const getInitialStateMessage: WorldMessage = {
      messageType: 'world',
      worldMessageType: 'get-initial-state'
    };
    const meetingState = callObject?.meetingState();
    if (meetingState === 'joined-meeting') {
      // sendAppMessage(getInitialStateMessage);
      isGetInitialStateMessageSent.current = true;
      // console.log('sent get initial state message');
    } else {
    }
  }

  const addUserMeshToReflectiveFloor = () => {
    if (groundReflectiveTexture.current === null || groundReflectiveTexture.current.renderList === null || limitQuality.current === true) return;
    groundReflectiveTexture.current.renderList = [];
    for (const user of userMeshManager.current.getUserMeshes()) {
      groundReflectiveTexture.current.renderList.push(user.head);
      groundReflectiveTexture.current.renderList.push(user.torso);
      groundReflectiveTexture.current.renderList.push(user.tv);
    }
  }

  const createInputListeners  = () => {
    // console.log('creating input listeners');
    createMouseListener();
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    const key = _.toLower(event.key);
    activeInputs.current[key] = event.type === 'keydown';
    updateIsMovementOrRotationKeyPressed();
    if (key === '1') onShouldEnableMic(!callObject?.localAudio() ?? false);
    if (key === '2') onShouldEnableCamera(!callObject?.localVideo() ?? false);

    if (key === 'e') setFocusedVolumeUsingShortestAngle();
    if (key === 'x') setDefaultVolumeForSound();
    if (key === 'f') setFocusedVolumeUsingShortestAngle();
    if (key === 'q') setDefaultVolumeForSound();
    // if (key === 'tab') {
    //   event.preventDefault();
    //   setFocusedVolumeUsingDefault();
    //   // setFocusedVolumeForSound();
    // } 
  }

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.type === 'keyup') delete activeInputs.current[_.toLower(event.key)];
    updateIsMovementOrRotationKeyPressed(); // do better?
  }

  // Keyboard Listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const updateIsMovementOrRotationKeyPressed = () => {
    const updateIsMovementKeyPressed = () => {
      const movementKeys = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown'];
      for (let key of movementKeys) {
        if (activeInputs.current[key]) {
          isMovementKeyPressed.current = true;
          return;
        }
      }
      isMovementKeyPressed.current = false;
    }

    const updateIsRotationKeyPressed = () => {
      const rotationKeys = ['arrowleft', 'arrowright'];
      for (let key of rotationKeys) {
        if (activeInputs.current[key]) {
          isRotationKeyPressed.current = true;
          return;
        }
      }
      isRotationKeyPressed.current = false;
    }

    updateIsMovementKeyPressed();
    updateIsRotationKeyPressed();
  }

  const createMouseListener = () => {
    if (sceneRef.current === null) return console.error('scene is null');
    sceneRef.current.onPointerObservable.add(onPointerObservableCallback);
  }

  const onPointerObservableCallback = async (pointerInfo: PointerInfo) => {
    switch (pointerInfo.type) {
      case PointerEventTypes.POINTERDOWN:
        const pickResult = sceneRef.current?.pick(pointerInfo.event.clientX, pointerInfo.event.clientY);
        if (!pickResult) break;
        // console.log(pickResult.distance, pickResult);
        if (!pickResult.pickedMesh || pickResult.pickedMesh.name.indexOf('seat') === -1) break;
        const pickedMeshSplicedName = pickResult.pickedMesh.name.split('.');
        if (pickedMeshSplicedName.length !== 2) break;
        const seatIndex = parseInt(pickedMeshSplicedName[1]);
        const seat = tableSeats.current[seatIndex];
        seatClicked(seatIndex, pickResult.distance);
      break;
      case PointerEventTypes.POINTERUP:
        delete activeInputs.current[`mouse-${pointerInfo.event.button}`];
        break;
      case PointerEventTypes.POINTERMOVE:
        break;
      case PointerEventTypes.POINTERWHEEL:
        break;
      case PointerEventTypes.POINTERPICK:
        break;
      case PointerEventTypes.POINTERTAP:
        break;
      case PointerEventTypes.POINTERDOUBLETAP:
        break;
    }
  }


  const calculateFps = () => {
    const currentTime = performance.now();
    const timeSinceLastRender = currentTime - prevRenderTime.current;

    prevRenderTime.current = currentTime;
    framesInCurrentCycle.current += 1;
    cumulativeRenderTime.current += timeSinceLastRender;
    currentFPS.current = 1000 * framesInCurrentCycle.current / cumulativeRenderTime.current;
    if (cumulativeRenderTime.current >= 1000) {
      updateFPS(currentFPS.current);
      rollingFPS.current.pop();
      rollingFPS.current.unshift(currentFPS.current);
      const averageFPS = rollingFPS.current.reduce((prev, current) => prev + current, 0) / rollingFPS.current.length;
      if (averageFPS < 25 && !limitQuality.current) {
        console.log('consistently low fps: ', averageFPS, ', lowering quality');
        limitQuality.current = true;
        groundReflectiveTexture.current.renderList = [];
      }
      framesInCurrentCycle.current = 0;
      cumulativeRenderTime.current = 0;
    }
    return timeSinceLastRender;
  }

  const render = () => {
    calculateFps();
    accumulateInput();
    implementInput();
    adjustCamera();
    floatUsers();
    updateAudioVisualCues();
    if (isMovementKeyPressed.current === true || isRotationKeyPressed.current === true || isMouseRotating.current === true) {
      updateAllUserSound();
      sendUserPositionAndRotationMessageToUsers();
      isMouseRotating.current = false;
      if (isMovementKeyPressed.current === true && localSeat.current !== -1) vacateSeat();
    }
    if (!isGetInitialStateMessageSent.current) {
      sendGetInitialStateMessage();
    }
    if (emptyRoomDisplayMessage.current) {
      emptyRoomDisplayMessage.current.rotation.y += 0.01;
    }
    // Do better
    // if (!hasReceivedInitialStateFromAllRemoteUsers.current && isGetInitialStateMessageSent.current) {
    //   console.log('waiting on initial state from all remote peers');
    //   hasReceivedInitialStateFromAllRemoteUsers.current = Object.values(remoteUserMesh.current).filter(u => u.user.hasReceivedInitialState === false).length === 0;
    //   if (hasReceivedInitialStateFromAllRemoteUsers.current) {
    //     const vacantSeatIndex = tableSeats.current.findIndex(seat => seat.userId === '');
    //     console.log('got init state from all users, auto assigning seat: ', vacantSeatIndex);
    //     if (vacantSeatIndex !== -1) {
    //       moveUserToSeat(vacantSeatIndex);
    //     }
    //   }
    // }
    if (sceneRef.current !== null) {
      sceneRef.current.render();
    }
  }

  const createDummyUsers = (scene: Scene) => {
    const material = new StandardMaterial(`material-test`, scene);
    material.diffuseColor = new Color3(1, 0, 0);

    const audioStreamSources = [VoiceTest, VoiceTest2, VoiceTest3, VoiceTest4];
    for (let i=1; i<2; i++) {
      for (let j=0; j<4; j++) {
        const fooU = new User(`foo-${i}-${j}`, `anon-${i}-${j}`, false, false);
        const fooUser = userMeshManager.current.addUser(fooU);
        fooUser.root.position = new Vector3(8 * j, 0.25, 1 * i);
    
        const nextStream = audioStreamSources.length > 0 ? audioStreamSources.pop() : SongTest;
        userMeshManager.current.attachTestAudioStreamToMesh(fooU.id, nextStream);
        userMeshManager.current.attachTestVideoStreamToMesh(fooU.id);
        updateUserSound(fooUser);
        fooUser.tv.rotation.z = Math.PI;

        updateTable();
        addUserMeshToReflectiveFloor();
      }
    }
  }

  const accumulateInput = () => {
    const maxMovement = getMaxMovement();
    const maxRotation = getMaxRotation();
    if (activeInputs.current.a) {
      movement.current.x = movement.current.x - microMovement();
    } else if(activeInputs.current.d) {
      movement.current.x = movement.current.x + microMovement();
    } else {
      movement.current.x = dampingFactor() * movement.current.x;
      movement.current.x = Math.abs(movement.current.x) >= 0.02 ? movement.current.x : 0;
    }
    movement.current.x = _.clamp(movement.current.x, -maxMovement, maxMovement);

    if (activeInputs.current.w || activeInputs.current['mouse-0'] || activeInputs.current.arrowup) {
      movement.current.y = movement.current.y + microMovement();
    } else if (activeInputs.current.s || activeInputs.current['mouse-2'] || activeInputs.current.arrowdown) {
      movement.current.y = movement.current.y - microMovement();
    } else {
      movement.current.y = dampingFactor() * movement.current.y;
      movement.current.y = Math.abs(movement.current.y) >= 0.02 ? movement.current.y : 0;
    }
    movement.current.y = _.clamp(movement.current.y, -maxMovement, maxMovement);

    if (activeInputs.current.arrowright || activeInputs.current.arrowleft) {
      rotation.current.y = rotation.current.y + (activeInputs.current.arrowright ? 1 : -1) * microRotation();
    } else {
      rotation.current.y = dampingFactor() * rotation.current.y;
      rotation.current.y = Math.abs(rotation.current.y) >= 0.02 ? rotation.current.y : 0;
    }
    rotation.current.y = _.clamp(rotation.current.y, -maxRotation, maxRotation);

    lastSentPositionAndRotationMessage.current = lastSentPositionAndRotationMessage.current ?? {
      worldMessageType: 'set-user-position-and-rotation',
      messageType: 'world',
      movement: new Vector2(0,0),
      headRotation: new Vector2(0,0),
      position: new Vector2(0,0),
    };

    handleWorldUserMessage(userMeshManager.current.localUserId, lastSentPositionAndRotationMessage.current); // necessary? or send with dummy id?
  }

  const implementInput = () => {
    const didMove = !!_.find([movement, rotation, mouseRotation], point => !!_.find(point));
    if(camera.current !== null) {
      if (rotation.current.y) camera.current.rotation.y = camera.current.rotation.y + rotation.current.y;
      cameraDirection.current.copyFromFloats(movement.current.x, 0, movement.current.y);
      if (didMove && camera.current._transformedDirection) {
        mouseRotation.current = new Vector2(0,0);
        camera.current.getViewMatrix().invertToRef(camera.current._cameraTransformMatrix);
        Vector3.TransformNormalToRef(cameraDirection.current, camera.current._cameraTransformMatrix, camera.current._transformedDirection);
        camera.current.cameraDirection.addInPlace(camera.current._transformedDirection);
      }
    }
  }

  const handleWorldUserMessage = (userId: string, message: WorldMessage) => {
    // console.log('handleWorldUserMessage', userId, localUserId, '<- userId & localUserId', message, userMesh.current);
    if (!(message.worldMessageType === 'set-user-position-and-rotation')) return;
    const worldMessage: SetUserPositionAndRotationWorldMessage = message as SetUserPositionAndRotationWorldMessage;
    if (userId === userMeshManager.current.localUserId) {
      // console.log('local message (for self)', userId, message);
      const rootMeshForLocalUser = getRootMeshForUser();
      rootMeshForLocalUser.position.x = worldMessage.position.x;
      rootMeshForLocalUser.position.z = worldMessage.position.y;
      rootMeshForLocalUser.rotation.y = worldMessage.headRotation.y;
    } else {
      const meshForUser = userMeshManager.current.getUserMesh(userId);
      if (!meshForUser) return;
      updateUserSound(meshForUser); // we should only update their userSound
      // console.log('foriegn pos/rot message', message);
      meshForUser.root.position.x = worldMessage.position.x;
      meshForUser.root.position.z = worldMessage.position.y;
      meshForUser.root.rotation.y = worldMessage.headRotation.y;
      meshForUser.headTransformNode.rotation.x = worldMessage.headRotation.x - (meshForUser.type !== 'UserMesh' ? Math.PI/2 : 0);
      meshForUser.torsoTransformNode.rotation.x = worldMessage.movement.y * 5;
      meshForUser.torsoTransformNode.rotation.z = -worldMessage.movement.x * 5;
    }
  }

  const sendUserPositionAndRotationMessageToUsers = ({ shouldForce = false, userId = '*' } = {}) => {
    if (camera.current !== null) {
      const message: SetUserPositionAndRotationWorldMessage = {
        messageType: 'world',
        worldMessageType: 'set-user-position-and-rotation',
        position: new Vector2(camera.current.position.x, camera.current.position.z),
        headRotation: new Vector2(camera.current.rotation.x, camera.current.rotation.y),
        movement: movement.current,
      };
      if (!shouldForce && _.isEqual(lastSentPositionAndRotationMessage, message)) return;
      if (callObject?.meetingState() === 'joined-meeting') {
        sendAppMessage(message, userId);
      }
      lastSentPositionAndRotationMessage.current = message;
    }
  }

  const sendUserTableValueMessageToUsers = (tableValue: TableValue, userId = '*') => {
    const message: SetUserTableValueWorldMessage = {
      messageType: 'world',
      worldMessageType: 'set-user-table-value',
      tablePosition: tableValue,
      seatedAt: new Date(),
    };
    if (callObject?.meetingState() === 'joined-meeting') {
      sendAppMessage(message, userId);
    }
  }

  const throttledSendMessage = _.throttle(() => {
    sendUserPositionAndRotationMessageToUsers();
  }, 1000 / 20);

  const debouncedSendMessage = _.debounce(() => {
    sendUserPositionAndRotationMessageToUsers();
  }, 1000 / 20);

  const floatUsers = () => {
    if (limitQuality.current === true) return;
    _.map(userMeshManager.current.getUserMeshes(), (user: UserMeshBasic | UserMeshMinimal) => {
      user.floatRadians += 0.02 * user.floatSpeed + 0.02;
      const bobHeight = Math.sin(user.floatRadians) * 0.02;
      user.root.position.y = 0.5 + bobHeight;
      if (camera.current !== null) {
        user.nameTag.rotation = camera.current.rotation.subtract(user.root.rotation);
      }
    });
  }

  const updateAudioVisualCues = () => {
    if (sceneRef.current === null) return;
    if (focusedUserIndicator.current) {
      if (!isFocusedSoundMode.current || focusedUserId.current === userMeshManager.current.localUserId) {
        sceneRef.current.removeMesh(focusedUserIndicator.current);
        if (focusedUserIndicator.current.material) focusedUserIndicator.current.material.dispose();
        focusedUserIndicator.current.dispose();
        focusedUserIndicator.current = undefined;
      }
      else {
        const focusedUserPosition = getRootMeshForUser(focusedUserId.current).position.clone();
        focusedUserPosition.y += 2;
        focusedUserIndicator.current.position = focusedUserPosition;
      }
    }
  }

  const setDefaultVolumeForSound = () => {
    focusedUserId.current = userMeshManager.current.localUserId;
    setFocusedVolumeForSound();
  }

  const setFocusedVolumeForSound = () => {
    isFocusedSoundMode.current = true;

    if (!focusedUserIndicator.current && sceneRef.current !== null && focusedUserId.current !== userMeshManager.current.localUserId) {
      focusedUserIndicator.current = MeshBuilder.CreateBox('focus-indicator', {
        width: 0.15,
        depth: 0.15,
        height: 0.3,
      }, sceneRef.current);

      const focusedIndicatorMaterial = new StandardMaterial("focalPointMaterial", sceneRef.current);
      focusedIndicatorMaterial.diffuseColor = new Color3(0, 1, 0); // Green color
      focusedUserIndicator.current.material = focusedIndicatorMaterial;
    }
    // TODO: Come up with clever ways of doing this?
    // Group people based on their distance to each other, and cycle through groups?
    updateAllUserSound();
  }

  const setFocusedVolumeUsingShortestAngle = () => {
    const sortedUserIdsByAngle = userMeshManager.current.sortUserIdsBySourceAngle(getRootMeshForUser());
    updateFocusedUserState(sortedUserIdsByAngle);
    setFocusedVolumeForSound();
  }

  const updateFocusedUserState = (newFocusedUserIdIterator: string[]) => {
    if (!shouldKeepCurrentFocusedUserIdIterator(newFocusedUserIdIterator)) focusedUserIdIterator.current = newFocusedUserIdIterator;
    let nextFocusedUserId = focusedUserIdIterator.current.pop();
    if (focusedUserIdIterator.current.length > 0 && nextFocusedUserId === focusedUserId.current) nextFocusedUserId = focusedUserIdIterator.current.pop(); // Is this necessary?
    if (!nextFocusedUserId) return;
    focusedUserId.current = nextFocusedUserId;
  }

  const shouldKeepCurrentFocusedUserIdIterator = (newFocusedUserIdIterator: string[]) => {
    if (focusedUserIdIterator.current.length === 0) return false;
    if (focusedUserIdIterator.current.length > newFocusedUserIdIterator.length) return false;
    for (const index in focusedUserIdIterator.current) {
      if (focusedUserIdIterator.current[index] !== newFocusedUserIdIterator[index]) return false;
    }
    return true;
  }

  const updateAllUserSound = () => {
    _.map(userMeshManager.current.getUserMeshes(), (user: UserMeshBasic | UserMeshMinimal) => {
      updateUserSound(user);
    });
    if (ytPlane.current !== null) {
      // have a HUD player & controls
      const distance = getDistance(camera.current.position, ytPlane.current.plane.position);
      const volume = 100 - 4 * distance;
      ytPlane.current.updateVolume(volume);
    }
  }

  const updateUserSound = (meshForUser: UserMeshBasic | UserMeshMinimal) => {
    if (meshForUser.micSound) {
      // TODO: For invert, should update InvertConfig to take maxDistance (and maybe min?) into account.
      const localUserRootMesh = getRootMeshForUser(userMeshManager.current.localUserId);
      if (isFocusedSoundMode.current) {
        const focusUserRootMesh = getRootMeshForUser(focusedUserId.current);
        meshForUser.micSound.updateAudioFilters(focusUserRootMesh.position, focusUserRootMesh.rotation, meshForUser.root.position, meshForUser.root.rotation, { lowpass: true, distance: true, source: true, target: true, pan: false });
        meshForUser.micSound.updateAudioFilters(localUserRootMesh.position, localUserRootMesh.rotation, meshForUser.root.position, meshForUser.root.rotation, { lowpass: false, distance: false, source: false, target: false, pan: true });
        // meshForUser.micSound.logAudioFilters(localUser.position, localUser.rotation, meshForUser.root.position, meshForUser.root.rotation);
      }
      else {
        meshForUser.micSound.updateAudioFilters(localUserRootMesh.position, localUserRootMesh.rotation, meshForUser.root.position, meshForUser.root.rotation);
        // meshForUser.micSound.logAudioFilters(localUser.position, localUser.rotation, meshForUser.root.position, meshForUser.root.rotation);
      }
    }
  }

  const isCameraOnStage = () => {
    const transformedCamPosition = camera.current.position.clone();
    transformedCamPosition.y = 0;
    const stageBoundingBox = stage.current.getBoundingInfo().boundingBox;
    const isCamOnStage = isPointIntersectingBox(transformedCamPosition, stageBoundingBox);
    if (isCamOnStage) {
      camera.current.position.y = cameraHeight.current + stageBoundingBox.maximumWorld.y;
    }
  }

  const adjustCamera = () => {
    if (camera.current !== null) {
      camera.current.position.y = cameraHeight.current; // fixed height position of camera
      
      const buffer = 10;
      camera.current.position.x = _.clamp(camera.current.position.x, boundsForWorld.current.min.x + buffer, boundsForWorld.current.max.x - buffer);
      camera.current.position.z = _.clamp(camera.current.position.z, boundsForWorld.current.min.z + buffer, boundsForWorld.current.max.z - buffer);

      // isCameraOnStage();
    }
  }

  const getRootMeshForUser = (userId: string = userMeshManager.current.localUserId) => {
    return userMeshManager.current.getRootMeshForUser(userId);
  }

  const getMaxMovement = () => {
    const maxMovement = 0.02;
    return maxMovement * shiftMultiple() * fpsMultiple();
  }

  const getMaxRotation = () => {
    const maxRotation = 0.03;
    return maxRotation * fpsMultiple();
  }

  const microMovement = () => {
    const microMovement = 0.005;
    return microMovement * shiftMultiple() * fpsMultiple();
  }

  const microRotation = () => {
    const microRotation = 0.01;
    return microRotation * shiftMultiple() * fpsMultiple();
  }

  const shiftMultiple = () => activeInputs.current.shift ? 1.5 : 0.75;

  const fpsMultiple = () => {
    return _.clamp(60 / currentFPS.current, 0.125, 8);
  }

  const dampingFactor = () => 0.8;

  return (
    <div className='world-container' id='world-container'>
      <canvas ref={canvasRef} style={{ width: '100%', height: '99.25vh' }} />
    </div>
  );
}

export default World;